import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { userThemeSelector } from '../../../store/user/user.selectors';
import { LEGACY_THEME_TYPES } from '../../styled/theme/ThemeProvider';
import { colors } from '../../styled/theme/constants/colors';

const DEFAULT_ICON_SIZE = 24;

/**
 * Optimized wrapper for imported SVG components with theme-aware styling
 * This approach uses direct SVG imports for better performance:
 * - Tree-shaking: Only used icons are bundled
 * - Build-time optimization: SVGs are processed at build time
 * - Better caching: Each SVG is a separate chunk
 * - Type safety: Import errors caught at build time
 * 
 * Default theme colors:
 * - Light theme: colors.Neutral11 
 * - Dark theme: colors.Neutral1
 * 
 * These defaults can be overridden per icon in the registry or via props.
 * 
 * Sizing behavior:
 * - When size === DEFAULT_ICON_SIZE (24): the SVG renders at its native/intrinsic dimensions.
 * - When size !== 24: the wrapper and SVG are explicitly sized to the given value.
 */

const StyledSvgWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${props => props.$fillColor};

    ${props => props.$forceSize ? `
        width: ${props.$size}px;
        height: ${props.$size}px;
    ` : ''}

    svg {
        ${props => props.$forceSize ? `
            width: 100%;
            height: 100%;
        ` : ''}

        ${props => !props.$preserveColors && `
            path, rect, polygon, circle {
                fill: ${props.$fillColor};
            }

            ellipse, line, polyline {
                stroke: ${props.$fillColor};
            }
        `}
    }
`;

const OptimizedCustomIcon = ({ 
    SvgComponent, 
    size = 24,
    color, 
    lightThemeFill, 
    darkThemeFill, 
    preserveColors = false,
    className, 
    style 
}) => {
    const theme = useSelector(userThemeSelector);
    
    // Determine fill color based on theme and props
    const getFillColor = () => {
        if (color) {
            return color; // Use provided color if specified
        }
        
        // Use theme-specific colors or consistent defaults
        const defaultLight = lightThemeFill || colors.Neutral11;
        const defaultDark = darkThemeFill || colors.Neutral1;
        
        return theme === LEGACY_THEME_TYPES.dark ? defaultDark : defaultLight;
    };
    
    const fillColor = getFillColor();

    const forceSize = size !== DEFAULT_ICON_SIZE;

    return (
        <StyledSvgWrapper 
            $size={size}
            $forceSize={forceSize}
            $fillColor={fillColor}
            $preserveColors={preserveColors}
            className={className}
            style={style}
        >
            <SvgComponent />
        </StyledSvgWrapper>
    );
};

OptimizedCustomIcon.propTypes = {
    SvgComponent: PropTypes.elementType.isRequired, // The imported SVG component
    size: PropTypes.number,
    color: PropTypes.string,
    lightThemeFill: PropTypes.string,
    darkThemeFill: PropTypes.string,
    preserveColors: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default OptimizedCustomIcon;
